import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import {
  listRickshaws,
  createRickshaw,
  updateRickshaw,
  deleteRickshaw,
  type RickshawWrite,
} from "@/lib/rickshaws-api";
import { sanitizePhone, isValidPhone, PHONE_INPUT_PROPS } from "@/lib/phone";

type R = {
  id?: string;
  driver_name: string;
  phone: string;
  vehicle_number: string;
  area: string;
  charges_per_trip: number;
  active: boolean;
};
const EMPTY: R = {
  driver_name: "",
  phone: "",
  vehicle_number: "",
  area: "",
  charges_per_trip: 150,
  active: true,
};

export const Route = createFileRoute("/admin/rickshaws")({ component: Page });

function Page() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<R | null>(null);

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-rickshaws"],
    queryFn: listRickshaws,
  });

  const toWrite = (d: R): RickshawWrite => ({
    driver_name: d.driver_name,
    phone: d.phone,
    vehicle_number: d.vehicle_number,
    area: d.area,
    charges_per_trip: d.charges_per_trip,
    active: d.active,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-rickshaws"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const createMut = useMutation({
    mutationFn: (d: R) => createRickshaw(toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Rickshaw created");
      setEdit(null);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (d: R) => updateRickshaw(d.id as string, toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Rickshaw updated");
      setEdit(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteRickshaw(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });

  const saving = createMut.isPending || updateMut.isPending;

  const save = () => {
    if (!edit) {
      return;
    }
    if (!isValidPhone(edit.phone)) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    if (edit.id) {
      updateMut.mutate(edit);
    } else {
      createMut.mutate(edit);
    }
  };
  const del = (id: string) => {
    if (confirm("Delete?")) {
      deleteMut.mutate(id);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl font-bold">Rickshaws</h2>
          <p className="text-sm text-muted-foreground">Auto rickshaw drivers for Yatra transport</p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY })} className="btn-primary py-2 text-sm">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-3">Driver</th>
              <th className="p-3">Vehicle</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Area</th>
              <th className="p-3">Trip ₹</th>
              <th className="p-3">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                  Loading rickshaws…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-sm text-destructive">
                  {error instanceof Error ? error.message : "Failed to load rickshaws"}
                </td>
              </tr>
            )}
            {!isLoading && !isError && list.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                  No rickshaws yet
                </td>
              </tr>
            )}
            {list.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                <td className="p-3 font-semibold">{r.driver_name}</td>
                <td className="p-3 font-mono text-xs">{r.vehicle_number}</td>
                <td className="p-3 text-muted-foreground">{r.phone}</td>
                <td className="p-3 text-muted-foreground">{r.area}</td>
                <td className="p-3">₹{r.charges_per_trip}</td>
                <td className="p-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.active ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}
                  >
                    {r.active ? "Active" : "Off"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEdit(r)} className="rounded-lg p-1.5 hover:bg-muted">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => r.id && del(r.id)}
                      className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
          onClick={() => setEdit(null)}
        >
          <div
            className="mx-auto my-8 w-full max-w-lg rounded-2xl bg-card p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-xl font-bold">{edit.id ? "Edit" : "Add"} Rickshaw</h3>
              <button onClick={() => setEdit(null)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Inp
                label="Driver Name"
                v={edit.driver_name}
                on={(x) => setEdit({ ...edit, driver_name: x })}
              />
              <Inp
                label="Phone"
                v={edit.phone}
                on={(x) => setEdit({ ...edit, phone: sanitizePhone(x) })}
                inputProps={{ ...PHONE_INPUT_PROPS, required: true }}
              />
              <Inp
                label="Vehicle Number"
                v={edit.vehicle_number}
                on={(x) => setEdit({ ...edit, vehicle_number: x.toUpperCase() })}
              />
              <Inp label="Area" v={edit.area} on={(x) => setEdit({ ...edit, area: x })} />
              <Inp
                label="Charges/trip"
                type="number"
                v={String(edit.charges_per_trip)}
                on={(x) => setEdit({ ...edit, charges_per_trip: +x })}
              />
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted-foreground">
                  Active
                </label>
                <select
                  className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                  value={String(edit.active)}
                  onChange={(e) => setEdit({ ...edit, active: e.target.value === "true" })}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setEdit(null)}
                disabled={saving}
                className="btn-outline py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Inp({
  label,
  v,
  on,
  type = "text",
  inputProps,
}: {
  label: string;
  v: string;
  on: (x: string) => void;
  type?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase text-muted-foreground">{label}</label>
      <input
        type={type}
        {...inputProps}
        value={v}
        onChange={(e) => on(e.target.value)}
        className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
      />
    </div>
  );
}
